import { formatMessage as defaultFormatMessage, type FormatMessageLike } from "./i18n-common";

export const SCORE_RECORD_SOURCE_VALUES = [
  "auto",
  "environment",
  "judge",
  "ai",
  "manual",
  "review",
] as const;

export type ScoreRecordSourceValue = (typeof SCORE_RECORD_SOURCE_VALUES)[number];

export const SCORE_RECORD_FINAL_SOURCE_VALUES = [
  "environment",
  "judge",
  "manual",
  "review",
] as const;

export type ScoreRecordFinalSourceValue = (typeof SCORE_RECORD_FINAL_SOURCE_VALUES)[number];

export const SCORE_RECORD_STATUS_VALUES = ["valid", "invalid", "pending"] as const;

export type ScoreRecordStatusValue = (typeof SCORE_RECORD_STATUS_VALUES)[number];

export const SCORE_RECORD_OBJECTIVE_QUESTION_TYPE_VALUES = [
  "single_choice",
  "multiple_choice",
  "judgment",
] as const;

export type ScoreRecordObjectiveQuestionTypeValue =
  (typeof SCORE_RECORD_OBJECTIVE_QUESTION_TYPE_VALUES)[number];

type ScoreRecordScoringInput = {
  source_type?: string | null;
  status?: string | null;
};

const SCORE_RECORD_SOURCE_LABEL_KEY_MAP: Record<ScoreRecordSourceValue, string> = {
  auto: "common.enums.scoreRecordSource.auto",
  environment: "common.enums.scoreRecordSource.environment",
  judge: "common.enums.scoreRecordSource.judge",
  ai: "common.enums.scoreRecordSource.ai",
  manual: "common.enums.scoreRecordSource.manual",
  review: "common.enums.scoreRecordSource.review",
};

const SCORE_RECORD_SOURCE_TAG_COLOR_MAP: Record<
  ScoreRecordSourceValue,
  "blue" | "cyan" | "geekblue" | "purple" | "orange" | "magenta"
> = {
  auto: "blue",
  environment: "cyan",
  judge: "geekblue",
  ai: "purple",
  manual: "orange",
  review: "magenta",
};

export const SCORE_RECORD_SOURCE_PRIORITY: Record<ScoreRecordSourceValue, number> = {
  auto: 1,
  environment: 2,
  judge: 3,
  ai: 4,
  manual: 5,
  review: 6,
};

const SCORE_RECORD_FINAL_SOURCE_VALUE_SET = new Set<string>(SCORE_RECORD_FINAL_SOURCE_VALUES);
const SCORE_RECORD_OBJECTIVE_QUESTION_TYPE_VALUE_SET = new Set<string>(
  SCORE_RECORD_OBJECTIVE_QUESTION_TYPE_VALUES,
);

const SCORE_RECORD_STATUS_LABEL_KEY_MAP: Record<ScoreRecordStatusValue, string> = {
  valid: "common.enums.scoreRecordStatus.valid",
  invalid: "common.enums.scoreRecordStatus.invalid",
  pending: "common.enums.scoreRecordStatus.pending",
};

const SCORE_RECORD_STATUS_TAG_COLOR_MAP: Record<
  ScoreRecordStatusValue,
  "success" | "default" | "processing"
> = {
  valid: "success",
  invalid: "default",
  pending: "processing",
};

const SCORE_RECORD_COMMENT_LABEL_KEY_MAP: Record<string, string> = {
  "auto objective scoring": "common.enums.scoreRecordComments.autoObjectiveScoring",
  "manual review": "common.enums.scoreRecordComments.manualReview",
  "flag validation passed": "common.enums.scoreRecordComments.flagValidationPassed",
  "flag validation failed": "common.enums.scoreRecordComments.flagValidationFailed",
  "flag validated at submit (deferred mode)":
    "common.enums.scoreRecordComments.flagValidatedDeferred",
  "flag validation failed at submit (deferred mode)":
    "common.enums.scoreRecordComments.flagValidationFailedDeferred",
  "no flag submitted": "common.enums.scoreRecordComments.noFlagSubmitted",
  "attack_defense flag snapshot missing":
    "common.enums.scoreRecordComments.attackDefenseFlagSnapshotMissing",
  "judge result": "common.enums.scoreRecordComments.judgeResult",
};

function normalizeScoreRecordValue(value?: string | null) {
  return String(value ?? "").trim();
}

function formatKnownScoreRecordLabel(
  normalizedValue: string,
  labelKeyMap: Record<string, string>,
  formatMessage: FormatMessageLike,
) {
  const labelKey = labelKeyMap[normalizedValue];
  if (labelKey) {
    return formatMessage({ id: labelKey });
  }
  if (normalizedValue) {
    return normalizedValue;
  }
  return formatMessage({ id: "common.labels.empty" });
}

export function getScoreRecordSourceLabel(
  value?: string | null,
  formatMessage: FormatMessageLike = defaultFormatMessage,
) {
  return formatKnownScoreRecordLabel(
    normalizeScoreRecordValue(value),
    SCORE_RECORD_SOURCE_LABEL_KEY_MAP,
    formatMessage,
  );
}

export function getScoreRecordSourceTagColor(value?: string | null) {
  const normalized = normalizeScoreRecordValue(value);
  return SCORE_RECORD_SOURCE_TAG_COLOR_MAP[normalized as ScoreRecordSourceValue] ?? "warning";
}

export function getScoreRecordSourcePriority(value?: string | null) {
  const normalized = normalizeScoreRecordValue(value);
  return SCORE_RECORD_SOURCE_PRIORITY[normalized as ScoreRecordSourceValue] ?? 0;
}

export function isFinalScoreRecordSource(value?: string | null) {
  return SCORE_RECORD_FINAL_SOURCE_VALUE_SET.has(normalizeScoreRecordValue(value));
}

export function buildScoreRecordSourceFilters(
  formatMessage: FormatMessageLike = defaultFormatMessage,
) {
  return SCORE_RECORD_SOURCE_VALUES.map((value) => ({
    text: getScoreRecordSourceLabel(value, formatMessage),
    value,
  }));
}

export function getScoreRecordStatusLabel(
  value?: string | null,
  formatMessage: FormatMessageLike = defaultFormatMessage,
) {
  return formatKnownScoreRecordLabel(
    normalizeScoreRecordValue(value),
    SCORE_RECORD_STATUS_LABEL_KEY_MAP,
    formatMessage,
  );
}

export function getScoreRecordStatusTagColor(value?: string | null) {
  const normalized = normalizeScoreRecordValue(value);
  return SCORE_RECORD_STATUS_TAG_COLOR_MAP[normalized as ScoreRecordStatusValue] ?? "warning";
}

export function isValidScoreRecordStatus(value?: string | null) {
  return normalizeScoreRecordValue(value) === "valid";
}

export function isObjectiveScoreQuestionType(value?: string | null) {
  return SCORE_RECORD_OBJECTIVE_QUESTION_TYPE_VALUE_SET.has(normalizeScoreRecordValue(value));
}

export function isScoreRecordCountedForAttemptQuestion(
  record: ScoreRecordScoringInput | undefined | null,
  questionType?: string | null,
): record is ScoreRecordScoringInput {
  if (!record || !isValidScoreRecordStatus(record.status)) {
    return false;
  }
  return isObjectiveScoreQuestionType(questionType) || isFinalScoreRecordSource(record.source_type);
}

export function getScoreRecordCommentLabel(
  value?: string | null,
  formatMessage: FormatMessageLike = defaultFormatMessage,
) {
  const normalized = normalizeScoreRecordValue(value);
  if (!normalized) return "-";
  const labelKey = SCORE_RECORD_COMMENT_LABEL_KEY_MAP[normalized];
  return labelKey ? formatMessage({ id: labelKey }) : normalized;
}
